import { Component, signal, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { SessionLifecycleService } from './shared/services/session-lifecycle';
import { MigrationInitializerService } from './shared/services/migration-initializer.service';
import { NotificationOverlay } from './shared/components/notification-overlay';
import { CheckoutSyncJobService } from './shared/services/checkout-sync-job.service';
import { UpdateBanner } from '@shared/components/update-banner/update-banner';
import { UpdateManagerService } from '@shared/services/update/update-manager';
import { CheckInLocationSyncService } from '@shared/services/checkin-location-sync';
import { IndexedDbService } from '@shared/services/indexed-db';
import { CheckoutSyncService } from '@shared/services/checkout-sync.service';
import { RemitoStateService } from '@shared/services/remito-state';
import { AppVersionService } from '@core/version/app-version.service';
import { environment } from '../environments/environment';

interface E2EBridge {
  clearState(): Promise<void>;
  seedCheckoutSession(remitoCount?: number): Promise<{ sessionId: string; count: number }>;
  pendingCount(): Promise<number>;
  hasStoredCheckIn(): Promise<boolean>;
  getLastCheckoutResult(): unknown;
  forceSyncAll(): Promise<string>;
}

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, NotificationOverlay, UpdateBanner],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App {
  private readonly sessionLifecycle = inject(SessionLifecycleService);
  private readonly migrationInitializer = inject(MigrationInitializerService);
  private readonly migrationReady = this.migrationInitializer.initialize();
  private readonly checkoutSyncJob = inject(CheckoutSyncJobService);
  private readonly checkoutSync = inject(CheckoutSyncService);
  private readonly updateManager = inject(UpdateManagerService);
  private readonly appVersion = inject(AppVersionService);
  private readonly checkInSync = inject(CheckInLocationSyncService);
  private readonly indexedDb = inject(IndexedDbService);
  private readonly remitoState = inject(RemitoStateService);
  protected readonly title = signal('remitos-pwa');

  constructor() {
    this.sessionLifecycle.init();
    this.checkoutSyncJob.start();
    this.updateManager.init();
    void this.appVersion.init();
    this.attachE2EBridge();
  }

  private attachE2EBridge(): void {
    if (environment.production) {
      return;
    }

    const testWindow = window as Window & { __E2E__?: E2EBridge };

    const clearState = async (): Promise<void> => {
      await this.migrationReady;
      await this.checkInSync.clearStoredCheckIns();
      await this.indexedDb.init();
      const all = await this.indexedDb.getAll();
      await Promise.all(all.map(item => this.indexedDb.delete(item.id)));
    };

    const seedCheckoutSession = async (remitoCount = 1): Promise<{ sessionId: string; count: number }> => {
      const count = Math.max(0, Math.floor(remitoCount));
      await clearState();

      const checkIn = await this.checkInSync.registrar(
        {
          latitud: -34.603722,
          longitud: -58.381592,
          precision: 12,
          timestamp: new Date().toISOString()
        },
        'fp-e2e-checkin',
        {
          deviceType: 'desktop',
          os: 'E2E',
          version: '1',
          mobile: false,
          resolution: '1920x1080',
          networkType: 'wifi',
          timeHour: 10,
          timezone: 'America/Argentina/Buenos_Aires',
          fingerprintId: 'fp-e2e-checkin'
        }
      );

      await this.indexedDb.init();

      const image = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8Xw8AAoMBgQv7FpQAAAAASUVORK5CYII=';

      const saveTasks = Array.from({ length: count }, async (_, index) => {
        const suffix = String(index + 1).padStart(2, '0');
        await this.indexedDb.savePending({
          id: `pending-${checkIn.sessionId}-${suffix}`,
          qrCode: `QR-E2E-${suffix}`,
          imageKeys: [`e2e-${suffix}.png`],
          images: [image],
          createdAt: Date.now(),
          sessionId: checkIn.sessionId,
          observation: `observacion-e2e-${suffix}`,
          checkIn: {
            id: checkIn.id,
            createdAt: new Date(checkIn.createdAt).toISOString(),
            location: {
              latitude: checkIn.ubicacion.latitud,
              longitude: checkIn.ubicacion.longitud,
              precision: checkIn.ubicacion.precision
            },
            deviceInfo: checkIn.deviceInfo ?? {
              deviceType: 'desktop',
              os: 'E2E',
              version: '1',
              mobile: false,
              resolution: '1920x1080',
              networkType: 'wifi',
              timeHour: 10,
              timezone: 'America/Argentina/Buenos_Aires',
              fingerprintId: 'fp-e2e-checkin'
            }
          }
        });
      });

      await Promise.all(saveTasks);
      return { sessionId: checkIn.sessionId, count };
    };

    const pendingCount = async (): Promise<number> => {
      await this.migrationReady;
      await this.indexedDb.init();
      const all = await this.indexedDb.getAll();
      return all.filter(item => item.status === 'pending' || item.status === 'syncing' || item.status === 'failed').length;
    };

    const hasStoredCheckIn = async (): Promise<boolean> => {
      await this.migrationReady;
      const checkIn = await this.checkInSync.getStoredCheckIn();
      return !!checkIn;
    };

    testWindow.__E2E__ = {
      clearState,
      seedCheckoutSession,
      pendingCount,
      hasStoredCheckIn,
      getLastCheckoutResult: () => this.checkoutSync.lastCheckoutResult(),
      forceSyncAll: () => this.remitoState.forceSyncAll()
    };
  }
}
